import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Image } from 'react-native';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '@react-navigation/native';
import { useStudentProfilePhoto } from '../hooks/useStudentProfilePhoto';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { WebPageShell } from '../components/common/WebPageShell';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { HelpCenterModal } from '../components/settings/HelpCenterModal';

type NavigationProp = NativeStackNavigationProp<any>;

interface MenuItemProps {
  icon: string;
  title: string;
  subtitle?: string;
  onPress: () => void;
  isLast?: boolean;
}

const MenuItem = ({ icon, title, subtitle, onPress, isLast }: MenuItemProps) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  return (
    <TouchableOpacity style={[styles.menuItem, !isLast && styles.menuItemBorder]} onPress={onPress}>
      <View style={[styles.menuIcon, { backgroundColor: `${['#EFF6FF', '#F0FDF4', '#FEF3C7', '#FCE7F3'][Math.floor(Math.random() * 4)]}` }]}>
        <FeatherIcon name={icon as any} size={18} color={colors.textPrimary} />
      </View>
      <View style={styles.menuText}>
        <Text style={styles.menuTitle}>{title}</Text>
        {subtitle && <Text style={styles.menuSubtitle}>{subtitle}</Text>}
      </View>
      <FeatherIcon name="chevron-right" size={18} color={colors.textMuted} />
    </TouchableOpacity>
  );
};

export function MoreScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { selectedStudent } = useAuth();
  const { displayPhoto } = useStudentProfilePhoto(selectedStudent?.id || '', selectedStudent || undefined);
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [showHelp, setShowHelp] = useState(false);

  return (
    <WebPageShell maxWidth={760}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View style={styles.profileCard}>
          <View style={styles.profileRow}>
            <View style={styles.avatarWrapper}>
              {displayPhoto ? (
                <Image source={{ uri: displayPhoto }} style={styles.avatar as any} />
              ) : (
                <Text style={styles.avatarPlaceholder}>
                  {selectedStudent?.name.charAt(0).toUpperCase()}
                </Text>
              )}
            </View>
            <View style={styles.profileInfo}>
              <Text style={styles.profileName}>{selectedStudent?.name}</Text>
              <Text style={styles.profileClass}>Grade {selectedStudent?.class}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Academic</Text>
          <View style={styles.card}>
            <MenuItem
              icon="book"
              title="Homework"
              subtitle="What's due and when"
              onPress={() => navigation.navigate('Homework' as any)}
            />
            <MenuItem
              icon="calendar"
              title="Exams"
              subtitle="Schedule & results"
              onPress={() => navigation.navigate('Academics' as any)}
            />
            <MenuItem
              icon="grid"
              title="Timetable"
              subtitle="Class schedule"
              onPress={() => navigation.navigate('Timetable' as any)}
            />
            {/* Attendance and Leave requests are separate pages, listed separately. */}
            <MenuItem
              icon="check-square"
              title="Attendance"
              subtitle="Daily & monthly reports"
              onPress={() => navigation.navigate('Attendance' as any)}
            />
            <MenuItem
              icon="edit-3"
              title="Leave Requests"
              subtitle="Apply & view history"
              onPress={() => navigation.navigate('Leaves' as any)}
              isLast
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Parent Portal</Text>
          <View style={styles.card}>
            <MenuItem
              icon="message-circle"
              title="Messages"
              subtitle="Chat with teachers"
              onPress={() => navigation.navigate('Chat' as any)}
            />
            <MenuItem
              icon="alert-circle"
              title="Complaints"
              subtitle="Raise concerns"
              onPress={() => navigation.navigate('Complaints' as any)}
            />
            <MenuItem
              icon="dollar-sign"
              title="Wallet"
              subtitle="Balance, top-ups & pay"
              onPress={() => navigation.navigate('Wallet' as any)}
            />
            <MenuItem
              icon="coffee"
              title="Canteen"
              subtitle="Order today's meals"
              onPress={() => navigation.navigate('Canteen' as any)}
              isLast
            />
            {/* Documents hidden for now - put isLast back on it when switching it on.
            <MenuItem
              icon="award"
              title="Documents"
              subtitle="Request certificates and download documents"
              onPress={() => navigation.navigate('Documents' as any)}
              isLast
            />
            */}
            {/* Transport hidden until the backend has a real transport module.
            <MenuItem
              icon="briefcase"
              title="Transport"
              subtitle="Bus tracking"
              onPress={() => navigation.navigate('Transport' as any)}
            />
            <MenuItem
              icon="log-out"
              title="Early Pickup"
              subtitle="Request an early release"
              onPress={() => navigation.navigate('EarlyPickup' as any)}
              isLast
            /> */}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Account</Text>
          <View style={styles.card}>
            <MenuItem
              icon="settings"
              title="Settings"
              subtitle="App preferences"
              onPress={() => navigation.navigate('Settings' as any)}
            />
            <MenuItem
              icon="help-circle"
              title="Help & Support"
              subtitle="How to use the app, and who to call"
              onPress={() => setShowHelp(true)}
              isLast
            />
          </View>
        </View>
      </ScrollView>
      <HelpCenterModal visible={showHelp} onClose={() => setShowHelp(false)} />
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  profileCard: { backgroundColor: colors.card, borderRadius: 12, padding: 16, marginBottom: 20, elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  profileRow: { flexDirection: 'row', alignItems: 'center' },
  avatarWrapper: { width: 56, height: 56, borderRadius: 28, backgroundColor: colors.border, justifyContent: 'center', alignItems: 'center', marginRight: 12, overflow: 'hidden' },
  avatar: { width: 56, height: 56, borderRadius: 28 },
  avatarPlaceholder: { fontSize: 22, fontWeight: '800', color: colors.primary },
  profileInfo: { flex: 1 },
  profileName: { fontSize: 15, fontWeight: '700', color: colors.textPrimary, marginBottom: 2 },
  profileClass: { fontSize: 12, color: colors.textMuted },
  section: { marginBottom: 20 },
  sectionTitle: { fontSize: 12, fontWeight: '700', color: colors.textMuted, textTransform: 'uppercase', marginBottom: 8, letterSpacing: 0.5 },
  card: { backgroundColor: colors.card, borderRadius: 12, overflow: 'hidden', elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  menuItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, paddingHorizontal: 16, gap: 12 },
  menuItemBorder: { borderBottomWidth: 1, borderBottomColor: colors.border },
  menuIcon: { width: 40, height: 40, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  menuText: { flex: 1 },
  menuTitle: { fontSize: 14, fontWeight: '700', color: colors.textPrimary },
  menuSubtitle: { fontSize: 11, color: colors.textMuted, marginTop: 2 },
});
